import { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import createDOMPurify from "@/vendor/purify.es.mjs";
import { marked } from "@/vendor/marked.esm.js";
import { API_BASE_URL, apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";
import { Button, Toggle } from "@/components/ui";
import { Icon } from "@/components/IconSprite";

type PlaygroundRole = "guest" | "business_owner" | "preferred_member" | "product_center" | "branch_operator" | "admin";

type UsernameSuggestion = {
  id: string;
  username: string;
};

type MemberSearchResponse = {
  results: UsernameSuggestion[];
};

type Citation = {
  index: number;
  document_id: string;
  source: string;
  snippet: string;
  score: number;
};

type Message = {
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  citations?: Citation[];
};

type ChatResponse = {
  reply: string;
  session_id: string;
  citations: Citation[];
};

const ChatPlayground = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // The chat stream does not echo a session_id back, so the client must own
  // the session identity: generate one up front and send it on every message,
  // otherwise each send starts a fresh conversation and context is lost.
  const newSessionId = () =>
    `playground-${(crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)}`;
  const [sessionId, setSessionId] = useState<string | null>(() => newSessionId());
  const [role, setRole] = useState<PlaygroundRole>("business_owner");
  const profile = useAuth((state) => state.profile);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberOptions, setMemberOptions] = useState<UsernameSuggestion[]>([]);
  const [selectedMember, setSelectedMember] = useState<UsernameSuggestion | null>(null);
  const [isSearchingMembers, setIsSearchingMembers] = useState(false);
  const [memberSearchError, setMemberSearchError] = useState<string | null>(null);
  const [showMemberSuggestions, setShowMemberSuggestions] = useState(false);
  const [justification, setJustification] = useState("");
  const [instructions, setInstructions] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [showImpersonate, setShowImpersonate] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  // Live progress line for the in-flight assistant message ("Checking live
  // data sources…"). Fed by the stream's progress events; cleared when the
  // first content delta arrives or the request settles.
  const [progressText, setProgressText] = useState<string | null>(null);
  const [useStreaming, setUseStreaming] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem("chat_playground_use_stream");
    return stored !== "0";
  });
  const threadRef = useRef<HTMLDivElement | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const dompurify = useMemo(() => {
    if (typeof window === "undefined") return null;
    return createDOMPurify(window);
  }, []);

  const canImpersonate = role !== "guest";
  const actorRole = useMemo(() => {
    const raw = profile?.role;
    if (!raw) return "";
    const normalized = raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
    if (["branch_operator"].includes(normalized)) return "branch_operator";
    if (["product_center", "stockist"].includes(normalized)) return "product_center";
    if (["business_owner", "member", "user"].includes(normalized)) return "business_owner";
    return normalized;
  }, [profile?.role]);
  const actingAsSelf = Boolean(
    selectedMember && profile?.userId && selectedMember.id === profile.userId
  );
  const requiresJustification = Boolean(
    selectedMember &&
    ["branch_operator", "product_center"].includes(actorRole) &&
    !actingAsSelf
  );

  useEffect(() => {
    if (!canImpersonate) {
      setMemberSearch("");
      setSelectedMember(null);
      setJustification("");
      setMemberOptions([]);
      setMemberSearchError(null);
      setShowImpersonate(false);
    }
  }, [canImpersonate]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("chat_playground_use_stream", useStreaming ? "1" : "0");
  }, [useStreaming]);

  // Keep the thread pinned to the latest message.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, progressText]);

  useEffect(() => {
    if (!canImpersonate) return;
    const term = memberSearch.trim();
    if (term.length < 2) {
      setMemberOptions([]);
      setMemberSearchError(null);
      setIsSearchingMembers(false);
      return;
    }
    const controller = new AbortController();
    let ignore = false;
    setIsSearchingMembers(true);
    setMemberSearchError(null);

    const params = new URLSearchParams({ term, limit: "10" });

    apiRequest<MemberSearchResponse>(`/api/admin/members/autocomplete?${params.toString()}`, {
      method: "GET",
      signal: controller.signal,
    })
      .then((payload) => {
        if (ignore) return;
        const mapped = payload?.results ?? [];
        setMemberOptions(mapped);
        if (mapped.length === 0) {
          setMemberSearchError("No users found with that username.");
        }
      })
      .catch((error) => {
        if (ignore || controller.signal.aborted) return;
        const message = error instanceof Error ? error.message : "Search failed.";
        setMemberSearchError(message);
        setMemberOptions([]);
      })
      .finally(() => {
        if (ignore) return;
        setIsSearchingMembers(false);
      });

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [memberSearch, canImpersonate]);

  const renderMarkdown = (value: string) => {
    if (!value || !value.trim()) return "";
    if (!dompurify) return value;
    const rawHtml = marked.parse(value, { mangle: false, headerIds: false });
    const html = typeof rawHtml === "string" ? rawHtml : String(rawHtml);
    return dompurify.sanitize(html, {
      ALLOWED_TAGS: [
        "a", "abbr", "blockquote", "br", "code", "em",
        "h1", "h2", "h3", "h4", "h5", "h6", "hr", "input", "li", "ol", "p", "pre",
        "strong", "sub", "sup", "table", "tbody", "td", "th", "thead", "tr", "ul",
      ],
      ALLOWED_ATTR: [
        "href", "title", "target", "rel", "src", "alt", "width", "height",
        "colspan", "rowspan", "type", "checked", "disabled",
      ],
      ALLOW_DATA_ATTR: false,
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["script", "style", "iframe"],
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!input.trim()) return;
    const question = input.trim();
    const trimmedJustification = justification.trim();
    const trimmedInstructions = instructions.trim();
    if (requiresJustification && !trimmedJustification) {
      setFormError("Please provide a justification before impersonating another member.");
      setShowImpersonate(true);
      return;
    }
    setFormError(null);
    setShowMemberSuggestions(false);

    const userTimestamp = Date.now();
    const userEntry: Message = { role: "user", content: question, timestamp: userTimestamp };
    const assistantTimestamp = userTimestamp + 1;
    let assistantIndex = -1;
    setMessages((prev) => {
      const next = [
        ...prev,
        userEntry,
        { role: "assistant", content: "", timestamp: assistantTimestamp },
      ];
      assistantIndex = next.length - 1;
      return next;
    });
    setInput("");
    if (taRef.current) taRef.current.style.height = "auto";
    setIsLoading(true);
    setProgressText("Sending…");

    const updateAssistant = (content: string, citations?: Citation[]) => {
      if (assistantIndex < 0) return;
      setMessages((prev) =>
        prev.map((message, index) =>
          index === assistantIndex
            ? {
                ...message,
                content,
                citations: citations !== undefined ? citations : message.citations,
              }
            : message,
        ),
      );
    };

    try {
      const body: Record<string, unknown> = {
        message: question,
        session_id: sessionId ?? undefined,
        meta: { lang: "en", role },
      };

      if (selectedMember) {
        (body.meta as Record<string, unknown>).target_user_id = selectedMember.id;
      }
      if (trimmedJustification) {
        (body.meta as Record<string, unknown>).justification = trimmedJustification;
      }
      if (trimmedInstructions) {
        (body.meta as Record<string, unknown>).instructions = trimmedInstructions;
      }

      let finalReply = "";
      let finalSessionId: string | null = sessionId;
      let finalCitations: Citation[] = [];

      if (useStreaming) {
        const url = new URL("/api/chat?stream=1", API_BASE_URL);
        const response = await fetch(url, {
          method: "POST",
          body: JSON.stringify(body),
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (response.status === 401) throw new Error("Unauthorized");
        if (!response.ok) throw new Error("Assistant responded with an error");
        if (!response.body) throw new Error("Streaming not supported");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop() ?? "";
          for (const eventChunk of events) {
            const lines = eventChunk.split("\n");
            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith("data:")) continue;
              const dataStr = trimmed.slice(5).trim();
              if (!dataStr || dataStr === "[DONE]") continue;
              try {
                const parsed = JSON.parse(dataStr) as {
                  type?: string;
                  message?: string;
                  delta?: string;
                  reply?: string;
                  session_id?: string;
                  citations?: Citation[];
                };
                if (parsed.type === "progress" && typeof parsed.message === "string") {
                  setProgressText(parsed.message);
                }
                if (typeof parsed.delta === "string") {
                  finalReply += parsed.delta;
                  setProgressText(null);
                  updateAssistant(finalReply);
                }
                if (typeof parsed.reply === "string" && parsed.reply) {
                  finalReply = parsed.reply;
                  updateAssistant(finalReply);
                }
                finalSessionId = parsed.session_id ?? finalSessionId;
                if (parsed.citations) finalCitations = parsed.citations;
              } catch {
                /* skip malformed chunks */
              }
            }
          }
        }
        updateAssistant(finalReply, finalCitations);
      } else {
        const payload = await apiRequest<ChatResponse>("/api/chat", {
          method: "POST",
          body: JSON.stringify(body),
        });
        finalReply = payload.reply;
        if (payload.session_id) finalSessionId = payload.session_id;
        finalCitations = payload.citations ?? [];
        updateAssistant(finalReply, finalCitations);
      }

      if (finalSessionId) setSessionId(finalSessionId);
    } catch (error) {
      const message =
        error instanceof Error && error.message === "Unauthorized"
          ? "Your session has expired. Please sign in again."
          : "The assistant could not process this message. Please try again.";
      updateAssistant(message);
    } finally {
      setIsLoading(false);
      setProgressText(null);
    }
  };

  const handleComposerKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  const autoGrow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  };

  const roleOptions = [
    { value: "guest", label: "Guest" },
    { value: "business_owner", label: "Business Owner" },
    { value: "preferred_member", label: "Preferred Member" },
    { value: "product_center", label: "Product Center" },
    { value: "branch_operator", label: "Branch Operator" },
    { value: "admin", label: "Admin" },
  ];

  const actingLabel = selectedMember
    ? `${selectedMember.username} (#${selectedMember.id})`
    : "Admin session";

  return (
    <div className="page wide">
      <div className="play-shell">
        {/* ── Controls bar ── */}
        <div className="play-bar">
          <span className="ttl"><Icon name="chat" />Playground</span>
          {sessionId && (
            <span className="sess" title={sessionId}>{sessionId.slice(0, 18)}…</span>
          )}
          <span className="grow" />
          <select
            className="play-sel"
            value={role}
            onChange={(e) => setRole(e.target.value as PlaygroundRole)}
            title="Persona role for this conversation"
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {canImpersonate && (
            <button
              type="button"
              className={`play-pill${selectedMember || showImpersonate ? " on" : ""}`}
              onClick={() => setShowImpersonate((v) => !v)}
              title="Impersonate a member to validate live database answers"
            >
              <Icon name="user" />
              {actingLabel}
            </button>
          )}
          <button
            type="button"
            className={`play-pill${instructions.trim() || showInstructions ? " on" : ""}`}
            onClick={() => setShowInstructions((v) => !v)}
            title="Optional system instructions passed with each request"
          >
            <Icon name="txt" />
            Instructions
          </button>
          <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--ink-3)", fontWeight: 580 }}>
            Stream <Toggle checked={useStreaming} onChange={setUseStreaming} />
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setMessages([]);
              setSessionId(newSessionId());
            }}
          >
            <Icon name="refresh" />
            Reset
          </Button>
        </div>

        {/* ── Impersonation panel (collapsible) ── */}
        {canImpersonate && showImpersonate && (
          <div className="play-panel">
            <div className="row" style={{ gridTemplateColumns: "1fr auto", gap: 8, alignItems: "end", marginBottom: requiresJustification ? 10 : 0 }}>
              <div className="cbx">
                <label className="fl">Impersonate member</label>
                <input
                  className="inp"
                  value={memberSearch}
                  onChange={(e) => {
                    setMemberSearch(e.target.value);
                    setShowMemberSuggestions(true);
                  }}
                  onFocus={() => setShowMemberSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowMemberSuggestions(false), 120)}
                  placeholder="Type a username..."
                />
                {showMemberSuggestions && memberOptions.length > 0 && (
                  <div className="cbx-list">
                    {memberOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className="cbx-i"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setSelectedMember(option);
                          setMemberSearch(option.username);
                          setShowMemberSuggestions(false);
                          setMemberSearchError(null);
                          setMemberOptions([]);
                        }}
                      >
                        <span className="tx">
                          <b>{option.username}</b>
                          <span>#{option.id}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {showMemberSuggestions && !isSearchingMembers && memberOptions.length === 0 && memberSearchError && (
                  <div className="al w" style={{ marginTop: 6 }}>
                    <Icon name="info" />
                    <div className="cnt-x">{memberSearchError}</div>
                  </div>
                )}
                {isSearchingMembers && (
                  <span className="hint">Searching...</span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                style={{ height: 34 }}
                onClick={() => {
                  setSelectedMember(null);
                  setMemberSearch("");
                  setJustification("");
                  setMemberOptions([]);
                  setMemberSearchError(null);
                }}
              >
                Clear
              </Button>
            </div>

            {requiresJustification && (
              <div className="fg" style={{ marginBottom: 0 }}>
                <label className="fl">Justification <span className="req">*</span></label>
                <textarea
                  className="ta"
                  rows={2}
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="Explain why you are accessing this member's data."
                />
                <p className="hint">
                  Notes are stored with audit logs whenever live data is retrieved with impersonation.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── System instructions panel (collapsible) ── */}
        {showInstructions && (
          <div className="play-panel">
            <label className="fl">System Instructions <span className="opt">optional</span></label>
            <textarea
              className="ta mono"
              rows={2}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Provide additional guidance for the assistant..."
            />
            <p className="hint">
              Instructions are passed with the request to preview admin overrides.
            </p>
          </div>
        )}

        {/* ── Chat thread ── */}
        <div className="play-thread" ref={threadRef}>
          <div className="play-thread-in">
            {messages.length === 0 && (
              <div className="empty sm" style={{ margin: "auto", paddingTop: 60 }}>
                <div className="empty-i"><Icon name="chat" /></div>
                <p>Start the conversation with a prompt.</p>
              </div>
            )}
            {messages.map((message, index) => {
              if (message.role === "user") {
                return (
                  <div key={message.timestamp} className="msg-u">{message.content}</div>
                );
              }
              const isPending = isLoading && index === messages.length - 1;
              return (
                <div key={message.timestamp} className="msg-a">
                  <div className="av"><Icon name="chat" /></div>
                  <div className="body">
                    {message.content ? (
                      <div
                        className="rich"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
                      />
                    ) : null}
                    {isPending && (
                      <div className="msg-think" style={message.content ? { marginTop: 9 } : undefined}>
                        <span className="dots"><i /><i /><i /></span>
                        {!message.content && <span>{progressText ?? "Thinking…"}</span>}
                      </div>
                    )}
                    {message.citations && message.citations.length > 0 && (
                      <ul className="cites">
                        {message.citations.map((citation) => (
                          <li key={`${message.timestamp}-${citation.index}`}>
                            [{citation.index}] {dompurify?.sanitize(citation.source) ?? citation.source}: {dompurify?.sanitize(citation.snippet) ?? citation.snippet}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Composer ── */}
        <div className="play-composer">
          <form ref={formRef} onSubmit={handleSubmit} className="play-composer-in">
            <textarea
              ref={taRef}
              className="play-ta"
              rows={1}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                autoGrow();
              }}
              onKeyDown={handleComposerKey}
              placeholder="Ask anything..."
              disabled={isLoading || !profile}
            />
            <Button
              type="submit"
              disabled={
                isLoading ||
                !profile ||
                (requiresJustification && justification.trim().length === 0)
              }
              loading={isLoading}
            >
              <Icon name="send" />
              Send
            </Button>
          </form>
          {formError && (
            <div className="al e" style={{ maxWidth: 860, margin: "8px auto 0" }}>
              <Icon name="alert" />
              <div className="cnt-x">{formError}</div>
            </div>
          )}
          <p className="play-hint">Enter to send · Shift+Enter for a new line</p>
        </div>
      </div>
    </div>
  );
};

export default ChatPlayground;
